<div class="h-full bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 overflow-y-auto">
	<!-- Stats Cards -->
	<div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
		<div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
			<div class="text-2xl font-bold text-[#5d202d]">{{ stats().today_count || 0 }}</div>
			<div class="text-sm text-gray-600">Today's Appointments</div>
		</div>
		<div class="bg-blue-50 rounded-xl p-4 shadow-sm border border-blue-100">
			<div class="text-2xl font-bold text-blue-700">{{ stats().today_confirmed || 0 }}</div>
			<div class="text-sm text-blue-600">Confirmed</div>
		</div>
		<div class="bg-yellow-50 rounded-xl p-4 shadow-sm border border-yellow-100">
			<div class="text-2xl font-bold text-yellow-700">{{ stats().today_pending || 0 }}</div>
			<div class="text-sm text-yellow-600">Pending</div>
		</div>
		<div class="bg-green-50 rounded-xl p-4 shadow-sm border border-green-100">
			<div class="text-2xl font-bold text-green-700">{{ stats().this_week || 0 }}</div>
			<div class="text-sm text-green-600">This Week</div>
		</div>
	</div>

	<div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
		<!-- Calendar Section (2/3 width) -->
		<div class="lg:col-span-2 bg-white rounded-2xl shadow-lg p-6">
			<!-- Calendar Header -->
			<div class="flex items-center justify-between mb-6">
				<div class="flex items-center gap-4">
					<h2 class="text-2xl font-bold text-[#5d202d]">
						{{
							currentMonth().toLocaleDateString('en-US', {
								month: 'long',
								year: 'numeric',
							})
						}}
					</h2>
					<button
						(click)="goToToday()"
						class="px-4 py-2 text-sm bg-[#5d202d]/10 text-[#5d202d] rounded-lg hover:bg-[#5d202d]/20 transition-colors font-medium"
					>
						Today
					</button>
				</div>
				<div class="flex items-center gap-2">
					<button
						(click)="changeMonth(-1)"
						class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
					>
						<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M15 19l-7-7 7-7"
							/>
						</svg>
					</button>
					<button
						(click)="changeMonth(1)"
						class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
					>
						<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 5l7 7-7 7"
							/>
						</svg>
					</button>
				</div>
			</div>

			<!-- Calendar Grid -->
			<div class="grid grid-cols-7 gap-2">
				<!-- Day headers -->
				@for (day of ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']; track day) {
					<div class="text-center text-sm font-semibold text-gray-600 py-2">
						{{ day }}
					</div>
				}

				<!-- Calendar days -->
				@for (day of calendarData(); track day.date) {
					<button
						(click)="selectCalendarDate(day.date)"
						[class.bg-[#5d202d]]="selectedDate() === day.date"
						[class.text-white]="selectedDate() === day.date"
						[class.ring-2]="day.is_today && selectedDate() !== day.date"
						[class.ring-[#caa78a]]="day.is_today && selectedDate() !== day.date"
						class="relative aspect-square rounded-lg hover:bg-gray-100 transition-all group cursor-pointer flex flex-col items-center justify-center"
					>
						<span
							class="text-sm font-medium"
							[class.text-white]="selectedDate() === day.date"
						>
							{{ day.date.split('-')[2] }}
						</span>
						@if (day.count > 0) {
							<span
								class="absolute bottom-1 w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold"
								[class.bg-white]="selectedDate() === day.date"
								[class.text-[#5d202d]]="selectedDate() === day.date"
								[class.bg-[#5d202d]]="selectedDate() !== day.date"
								[class.text-white]="selectedDate() !== day.date"
							>
								{{ day.count }}
							</span>
						}
					</button>
				}
			</div>
		</div>

		<!-- Appointments List (1/3 width) -->
		<div class="bg-white rounded-2xl shadow-lg p-6 max-h-[600px] overflow-y-auto">
			<div class="flex items-center justify-between mb-6">
				<h3 class="text-lg font-bold text-gray-900">
					{{ selectedDate() | date: 'MMM d, yyyy' }}
				</h3>
				<span
					class="px-3 py-1 bg-[#5d202d]/10 text-[#5d202d] rounded-full text-sm font-semibold"
				>
					{{ selectedDateAppointments().length }}
				</span>
			</div>

			@if (isLoading()) {
				<div class="space-y-3">
					@for (i of [1, 2, 3]; track i) {
						<div class="animate-pulse bg-gray-100 h-24 rounded-lg"></div>
					}
				</div>
			} @else if (selectedDateAppointments().length === 0) {
				<div class="flex flex-col items-center justify-center py-12 text-center">
					<svg
						class="w-16 h-16 text-gray-300 mb-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
						/>
					</svg>
					<p class="text-gray-500 font-medium">No appointments</p>
					<p class="text-gray-400 text-sm">for this date</p>
				</div>
			} @else {
				<div class="space-y-3">
					@for (apt of selectedDateAppointments(); track apt.id) {
						<div
							class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
							(click)="viewDetails(apt)"
						>
							<div class="flex items-start justify-between mb-2">
								<div class="flex-1">
									<div class="flex items-center gap-2 mb-1">
										<span class="font-semibold text-gray-900">{{
											apt.formatted_time
										}}</span>
										<span
											[class]="statusColors[apt.status]"
											class="px-2 py-0.5 text-xs rounded-full font-medium"
										>
											{{ apt.status }}
										</span>
										@if (apt.is_consultation) {
											<span
												class="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 font-medium"
											>
												Consultation
											</span>
										}
									</div>
									<p class="font-medium text-[#5d202d]">{{ apt.full_name }}</p>
									@if (apt.service) {
										<p class="text-sm text-gray-600">
											{{ apt.service.service_name }}
										</p>
									}
									<p class="text-sm text-gray-500">
										with {{ apt.staff?.full_name || 'Staff' }}
									</p>
									@if (apt.customer?.phone) {
										<p class="text-xs text-gray-400 mt-1">
											{{ apt.customer.phone }}
										</p>
									}
								</div>
							</div>

							<!-- Actions -->
							<div
								class="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100"
								(click)="$event.stopPropagation()"
							>
								@if (apt.status === 'pending') {
									<button
										(click)="updateStatus(apt, 'confirmed')"
										class="flex-1 px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 font-medium"
									>
										Confirm
									</button>
								}
								@if (apt.status === 'confirmed') {
									<button
										(click)="updateStatus(apt, 'completed')"
										class="flex-1 px-3 py-1.5 text-xs bg-green-500 text-white rounded hover:bg-green-600 font-medium"
									>
										Complete
									</button>
								}
								@if (apt.status === 'pending' || apt.status === 'confirmed') {
									<button
										(click)="cancelAppointment(apt)"
										class="px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100 font-medium"
									>
										Cancel
									</button>
								}
							</div>
						</div>
					}
				</div>
			}
		</div>
	</div>
</div>

<!-- New Booking Modal Component -->
@if (showModal()) {
	<app-booking-modal (close)="onBookingModalClose()"></app-booking-modal>
}

<!-- Appointment Details Modal Template -->
<ng-template #appointmentDetailsModalTemplate>
	<div class="bg-white rounded-2xl shadow-2xl w-full animate-scale-in">
		@if (selectedAppointment(); as apt) {
			<!-- Header -->
			<div
				style="
					padding: 20px 24px;
					border-bottom: 1px solid #e5e7eb;
					display: flex;
					align-items: center;
					justify-content: space-between;
					flex-shrink: 0;
				"
			>
				<h3 style="font-size: 1.5rem; font-weight: 700; color: #5d202d; margin: 0">
					Appointment Details
				</h3>
				<button
					type="button"
					(click)="closeDetails()"
					style="
						padding: 8px;
						border-radius: 8px;
						border: none;
						background: transparent;
						cursor: pointer;
					"
				>
					<svg
						style="width: 24px; height: 24px; color: #6b7280"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				</button>
			</div>

			<!-- Body - Scrollable -->
			<div
				style="
					flex: 1;
					overflow-y: auto;
					padding: 24px;
					overscroll-behavior: contain;
					max-height: 70vh;
				"
			>
				<div class="space-y-6">
					<!-- Status Badge -->
					<div class="flex items-center gap-3">
						<span
							[class]="statusColors[apt.status]"
							class="px-3 py-1.5 text-sm rounded-full font-semibold"
						>
							{{ apt.status }}
						</span>
						@if (apt.is_consultation) {
							<span
								class="px-3 py-1.5 text-sm rounded-full bg-purple-100 text-purple-700 font-semibold"
							>
								Consultation
							</span>
						}
					</div>

					<!-- Customer Information -->
					<div class="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
						<h4 class="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
							<svg
								class="w-4 h-4 text-blue-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
								/>
							</svg>
							Customer Details
						</h4>
						<div class="space-y-2">
							<div class="flex items-center gap-2">
								<span class="text-xs text-gray-500 w-20">Name:</span>
								<span class="text-sm font-semibold text-gray-900">{{
									apt.full_name
								}}</span>
							</div>
							<div class="flex items-center gap-2">
								<span class="text-xs text-gray-500 w-20">Phone:</span>
								<span class="text-sm text-gray-700">{{ apt.phone }}</span>
							</div>
							@if (apt.email) {
								<div class="flex items-center gap-2">
									<span class="text-xs text-gray-500 w-20">Email:</span>
									<span class="text-sm text-gray-700">{{ apt.email }}</span>
								</div>
							}
						</div>
					</div>

					<!-- Appointment Information -->
					<div class="bg-green-50/50 rounded-xl p-4 border border-green-100">
						<h4 class="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
							<svg
								class="w-4 h-4 text-green-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
								/>
							</svg>
							Appointment Details
						</h4>
						<div class="space-y-2">
							<div class="flex items-center gap-2">
								<span class="text-xs text-gray-500 w-20">Date:</span>
								<span class="text-sm font-semibold text-gray-900">{{
									apt.appointment_date.split('T')[0]
								}}</span>
							</div>
							<div class="flex items-center gap-2">
								<span class="text-xs text-gray-500 w-20">Time:</span>
								<span class="text-sm text-gray-700">{{ apt.formatted_time }}</span>
							</div>
							<div class="flex items-center gap-2">
								<span class="text-xs text-gray-500 w-20">Duration:</span>
								<span class="text-sm text-gray-700"
									>{{ apt.duration_minutes }} minutes</span
								>
							</div>
							@if (apt.service) {
								<div class="flex items-center gap-2">
									<span class="text-xs text-gray-500 w-20">Service:</span>
									<span class="text-sm text-gray-700">{{
										apt.service.service_name
									}}</span>
								</div>
							}
							@if (apt.staff) {
								<div class="flex items-center gap-2">
									<span class="text-xs text-gray-500 w-20">Staff:</span>
									<span class="text-sm text-gray-700">{{
										apt.staff.full_name
									}}</span>
								</div>
							}
							@if (apt.total_amount && +apt.total_amount > 0) {
								<div class="flex items-center gap-2">
									<span class="text-xs text-gray-500 w-20">Amount:</span>
									<span class="text-sm font-semibold text-[#5d202d]"
										>₹{{ apt.total_amount }}</span
									>
								</div>
							}
							@if (apt.consultation_mode) {
								<div class="flex items-center gap-2">
									<span class="text-xs text-gray-500 w-20">Mode:</span>
									<span class="text-sm text-gray-700">{{
										apt.consultation_mode === 'online'
											? 'Online (Zoom)'
											: 'In-Person'
									}}</span>
								</div>
							}
						</div>
					</div>

					<!-- Zoom Meeting Link -->
					@if (apt.zoom_join_url && apt.status !== 'cancelled') {
						<div class="bg-blue-50 rounded-xl p-4 border border-blue-200">
							<h4
								class="text-sm font-bold text-blue-700 mb-3 flex items-center gap-2"
							>
								<svg
									class="w-4 h-4 text-blue-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
									/>
								</svg>
								Zoom Meeting
							</h4>
							<div class="flex items-center gap-2">
								<a
									[href]="apt.zoom_join_url"
									target="_blank"
									class="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold text-sm text-center flex items-center justify-center gap-2"
								>
									<svg
										class="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
										/>
									</svg>
									Join Zoom Meeting
								</a>
								<button
									(click)="copyZoomLink(apt.zoom_join_url)"
									class="px-3 py-2.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium text-sm"
									title="Copy Link"
								>
									<svg
										class="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
										/>
									</svg>
								</button>
							</div>
							<p class="text-xs text-blue-600 mt-2 break-all">
								{{ apt.zoom_join_url }}
							</p>
						</div>
					}

					<!-- Notes -->
					@if (apt.notes) {
						<div class="bg-yellow-50/50 rounded-xl p-4 border border-yellow-100">
							<h4 class="text-sm font-bold text-gray-700 mb-2">Notes</h4>
							<p class="text-sm text-gray-700">{{ apt.notes }}</p>
						</div>
					}

					<!-- Remarks -->
					@if (apt.remarks) {
						<div class="bg-orange-50/50 rounded-xl p-4 border border-orange-100">
							<h4
								class="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2"
							>
								<svg
									class="w-4 h-4 text-orange-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
									/>
								</svg>
								Remarks
							</h4>
							<p class="text-sm text-gray-700">{{ apt.remarks }}</p>
						</div>
					}

					<!-- Action Buttons -->
					<div class="flex gap-3 pt-4 border-t border-gray-200">
						@if (apt.status === 'pending') {
							<button
								(click)="closeDetails(); updateStatus(apt, 'confirmed')"
								class="flex-1 px-4 py-2.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold"
							>
								Confirm Appointment
							</button>
						}
						@if (apt.status === 'confirmed') {
							<button
								(click)="closeDetails(); updateStatus(apt, 'completed')"
								class="flex-1 px-4 py-2.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold"
							>
								Mark as Completed
							</button>
						}
						@if (apt.status === 'pending' || apt.status === 'confirmed') {
							<button
								(click)="closeDetails(); cancelAppointment(apt)"
								class="px-4 py-2.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-semibold border border-red-200"
							>
								Cancel
							</button>
						}
					</div>
				</div>
			</div>
		}
	</div>
</ng-template>

<!-- Remarks Modal -->
@if (showRemarksModal()) {
	<div
		style="
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			z-index: 999999;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 16px;
		"
	>
		<!-- Backdrop -->
		<div
			style="
				position: absolute;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				background: rgba(0, 0, 0, 0.5);
			"
			(click)="closeRemarksModal()"
		></div>

		<!-- Modal Box -->
		<div
			(click)="$event.stopPropagation()"
			style="
				position: relative;
				background: white;
				border-radius: 16px;
				width: 100%;
				max-width: 480px;
				box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
			"
		>
			<!-- Header -->
			<div class="px-6 py-5 border-b border-gray-200">
				<div class="flex items-center gap-3">
					@if (remarksAction()?.status === 'cancelled') {
						<div
							class="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center"
						>
							<svg
								class="w-5 h-5 text-red-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</div>
						<div>
							<h3 class="text-lg font-bold text-gray-900">Cancel Appointment</h3>
							<p class="text-sm text-gray-500">
								{{ remarksAction()?.appointment?.full_name }}
							</p>
						</div>
					} @else {
						<div
							class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center"
						>
							<svg
								class="w-5 h-5 text-green-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M5 13l4 4L19 7"
								/>
							</svg>
						</div>
						<div>
							<h3 class="text-lg font-bold text-gray-900">Complete Appointment</h3>
							<p class="text-sm text-gray-500">
								{{ remarksAction()?.appointment?.full_name }}
							</p>
						</div>
					}
				</div>
			</div>

			<!-- Body -->
			<div class="px-6 py-5">
				<label class="block text-sm font-semibold text-gray-700 mb-2"
					>Remarks <span class="text-gray-400 font-normal">(optional)</span></label
				>
				<textarea
					[value]="remarksText()"
					(input)="remarksText.set($any($event.target).value)"
					rows="4"
					class="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] outline-none resize-none transition-all"
					[placeholder]="
						remarksAction()?.status === 'cancelled'
							? 'Reason for cancellation...'
							: 'Any remarks about this appointment...'
					"
				></textarea>
			</div>

			<!-- Footer -->
			<div class="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
				<button
					(click)="closeRemarksModal()"
					class="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
				>
					Go Back
				</button>
				@if (remarksAction()?.status === 'cancelled') {
					<button
						(click)="submitRemarks()"
						class="px-5 py-2.5 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
					>
						Cancel Appointment
					</button>
				} @else {
					<button
						(click)="submitRemarks()"
						class="px-5 py-2.5 text-sm font-semibold text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors"
					>
						Mark as Completed
					</button>
				}
			</div>
		</div>
	</div>
}
